/**
 * SLA Catalogue Registry Parser
 * 
 * Parses the consolidated Markdown registry file (content/sla-catalogue.md)
 * and provides typed access to SLA configurations, channel SLAs, stage SLAs,
 * escalation rules, and exception codes.
 * 
 * This is the SINGLE SOURCE OF TRUTH for the SLA Catalogue.
 */

import yaml from 'js-yaml';
import type {
  SLACatalogueRegistry,
  SLAGlobalConfig,
  ChannelSLA,
  StageSLA,
  EscalationRule,
  SLAExceptionCode,
  PipelineBrand,
  LeadChannel,
  SLAPriority,
  SLABehavior,
  EscalationTriggerType,
} from '../types';
import { getStageById } from './leadPipelineRegistry';

// Import the raw markdown content
import slaCatalogueMarkdown from '../../content/sla-catalogue.md?raw';

// =============================================================================
// Parsing Utilities
// =============================================================================

/**
 * Parse YAML block from fenced code block
 */
function parseYamlBlock(content: string): Record<string, unknown> | null {
  const yamlMatch = content.match(/```yaml\n([\s\S]*?)\n```/);
  if (!yamlMatch) return null;
  
  try {
    return yaml.load(yamlMatch[1]) as Record<string, unknown>;
  } catch (e) {
    console.error('Failed to parse YAML block:', e);
    return null;
  }
}

/**
 * Extract section content by heading (## level)
 * Captures everything until the next ## heading (not ### subheading)
 */
function extractMainSection(content: string, heading: string): string {
  // Find the start of the section
  const startPattern = new RegExp(`## ${heading}`, 'i');
  const startMatch = content.match(startPattern);
  if (!startMatch || startMatch.index === undefined) return '';
  
  const startIdx = startMatch.index;
  const afterStart = content.substring(startIdx + startMatch[0].length);
  
  // Find the next ## heading that is NOT a ### subheading
  // Look for "\n## " followed by a non-# character
  const nextSectionMatch = afterStart.match(/\n## (?!#)/);
  
  if (nextSectionMatch && nextSectionMatch.index !== undefined) {
    return content.substring(startIdx, startIdx + startMatch[0].length + nextSectionMatch.index);
  }
  
  // No next section found, return rest of content
  return content.substring(startIdx);
}

// Note: extractSubSection is available for future subsection parsing needs
// function extractSubSection(content: string, heading: string): string {
//   const headingPattern = new RegExp(`### ${heading}[\\s\\S]*?(?=### |## |$)`, 'i');
//   const match = content.match(headingPattern);
//   if (!match) return '';
//   return match[0];
// }

// =============================================================================
// Document Info Parser
// =============================================================================

interface DocumentInfo {
  version: string;
  lastUpdated: string;
  description: string;
  descriptionAr: string;
}

function parseDocumentInfo(content: string): DocumentInfo {
  const section = extractMainSection(content, 'DOCUMENT INFO');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.info) {
    console.warn('SLA Catalogue: Missing document info section');
    return {
      version: '1.0.0',
      lastUpdated: new Date().toISOString().split('T')[0],
      description: '',
      descriptionAr: '',
    };
  }
  
  const info = yamlData.info as Record<string, unknown>;
  
  return {
    version: String(info.version || '1.0.0'),
    lastUpdated: String(info.lastUpdated || new Date().toISOString().split('T')[0]),
    description: String(info.description || ''),
    descriptionAr: String(info.descriptionAr || ''),
  };
}

// =============================================================================
// Global Configuration Parser
// =============================================================================

function parseGlobalConfig(content: string): SLAGlobalConfig {
  const section = extractMainSection(content, 'GLOBAL CONFIGURATION');
  const yamlData = parseYamlBlock(section);
  
  if (!yamlData || !yamlData.globalConfig) {
    console.warn('SLA Catalogue: Missing global configuration section');
    return {
      timezone: 'Asia/Riyadh',
      businessHours: {
        start: '08:00',
        end: '17:00',
        days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        daysAr: ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس'],
      },
      afterHoursPolicy: '',
      afterHoursPolicyAr: '',
      slaPauseConditions: [],
      slaPauseConditionsAr: [],
      slaResumeConditions: [],
      slaResumeConditionsAr: [],
    };
  }
  
  const config = yamlData.globalConfig as Record<string, unknown>;
  const businessHours = config.businessHours as Record<string, unknown> || {};
  
  return {
    timezone: String(config.timezone || 'Asia/Riyadh'),
    businessHours: {
      start: String(businessHours.start || '08:00'),
      end: String(businessHours.end || '17:00'),
      days: (businessHours.days as string[]) || [],
      daysAr: (businessHours.daysAr as string[]) || [],
    },
    afterHoursPolicy: String(config.afterHoursPolicy || ''),
    afterHoursPolicyAr: String(config.afterHoursPolicyAr || ''),
    slaPauseConditions: (config.slaPauseConditions as string[]) || [],
    slaPauseConditionsAr: (config.slaPauseConditionsAr as string[]) || [],
    slaResumeConditions: (config.slaResumeConditions as string[]) || [],
    slaResumeConditionsAr: (config.slaResumeConditionsAr as string[]) || [],
  };
}

// =============================================================================
// Channel SLAs Parser
// =============================================================================

function parseChannelSLAs(content: string): ChannelSLA[] {
  const section = extractMainSection(content, 'CHANNEL SLAs');
  if (!section) {
    console.warn('SLA Catalogue: Missing CHANNEL SLAs section');
    return [];
  }
  
  const channelSLAs: ChannelSLA[] = [];
  
  // Find all YAML blocks with meta in this section
  // Pattern: ```yaml followed by newline, then meta: on next line
  const yamlBlockPattern = /```yaml\nmeta:\n([\s\S]*?)```/g;
  let match;
  
  while ((match = yamlBlockPattern.exec(section)) !== null) {
    try {
      const yamlContent = `meta:\n${match[1]}`;
      const yamlData = yaml.load(yamlContent) as Record<string, unknown>;
      
      if (!yamlData || !yamlData.meta) continue;
      
      const meta = yamlData.meta as Record<string, unknown>;
      
      // Skip if this doesn't look like a channel SLA (has channelType field)
      if (!meta.channelType) continue;
      
      const channelSLA: ChannelSLA = {
        id: String(meta.id || ''),
        channel: String(meta.channel || ''),
        channelAr: String(meta.channelAr || ''),
        channelType: (meta.channelType as LeadChannel) || 'website',
        timeToFirstAction: String(meta.timeToFirstAction || ''),
        timeToFirstActionAr: String(meta.timeToFirstActionAr || ''),
        timeToFirstContact: String(meta.timeToFirstContact || ''),
        timeToFirstContactAr: String(meta.timeToFirstContactAr || ''),
        requiredAttempts: Number(meta.requiredAttempts) || 0,
        attemptCadence: String(meta.attemptCadence || ''),
        attemptCadenceAr: String(meta.attemptCadenceAr || ''),
        owner: String(meta.owner || ''),
        ownerAr: String(meta.ownerAr || ''),
        appliesToBrands: (meta.appliesToBrands as PipelineBrand[]) || ['Changan', 'Hyundai', 'Stellantis'],
        priority: (meta.priority as SLAPriority) || 'medium',
        notes: String(meta.notes || ''),
        notesAr: String(meta.notesAr || ''),
      };
      
      if (channelSLA.id) {
        channelSLAs.push(channelSLA);
      }
    } catch (e) {
      console.error('Failed to parse channel SLA:', e);
    }
  }
  
  return channelSLAs;
}

// =============================================================================
// Stage SLAs Parser
// =============================================================================

function parseStageSLAs(content: string): StageSLA[] {
  const section = extractMainSection(content, 'STAGE SLAs');
  if (!section) {
    console.warn('SLA Catalogue: Missing STAGE SLAs section');
    return [];
  }
  
  const stageSLAs: StageSLA[] = [];
  
  // Find all YAML blocks with meta in this section
  // Pattern: ```yaml followed by newline, then meta: on next line
  const yamlBlockPattern = /```yaml\nmeta:\n([\s\S]*?)```/g;
  let match;
  
  while ((match = yamlBlockPattern.exec(section)) !== null) {
    try {
      const yamlContent = `meta:\n${match[1]}`;
      const yamlData = yaml.load(yamlContent) as Record<string, unknown>;
      
      if (!yamlData || !yamlData.meta) continue;
      
      const meta = yamlData.meta as Record<string, unknown>;
      
      // Skip if this doesn't look like a stage SLA (has stageId field)
      if (!meta.stageId) continue;
      
      const stageId = String(meta.stageId || '');
      
      // Validate stageId exists in pipeline registry
      if (stageId && stageId !== 'delivery_post_sale') {
        const pipelineStage = getStageById(stageId);
        if (!pipelineStage) {
          console.warn(`SLA Catalogue: Stage ID "${stageId}" not found in Lead Pipeline registry`);
        }
      }
      
      const stageSLA: StageSLA = {
        id: String(meta.id || ''),
        stageId: stageId,
        stageName: String(meta.stageName || ''),
        stageNameAr: String(meta.stageNameAr || ''),
        maxDuration: String(meta.maxDuration || ''),
        maxDurationAr: String(meta.maxDurationAr || ''),
        targetDuration: String(meta.targetDuration || ''),
        targetDurationAr: String(meta.targetDurationAr || ''),
        firstActionSLA: String(meta.firstActionSLA || ''),
        firstActionSLAAr: String(meta.firstActionSLAAr || ''),
        followUpSLA: String(meta.followUpSLA || ''),
        followUpSLAAr: String(meta.followUpSLAAr || ''),
        handoverSLA: meta.handoverSLA ? String(meta.handoverSLA) : undefined,
        handoverSLAAr: meta.handoverSLAAr ? String(meta.handoverSLAAr) : undefined,
        owner: String(meta.owner || ''),
        ownerAr: String(meta.ownerAr || ''),
        appliesToBrands: (meta.appliesToBrands as PipelineBrand[]) || ['Changan', 'Hyundai', 'Stellantis'],
        kpiReferences: (meta.kpiReferences as string[]) || [],
        escalationTrigger: String(meta.escalationTrigger || ''),
        escalationTriggerAr: String(meta.escalationTriggerAr || ''),
      };
      
      if (stageSLA.id) {
        stageSLAs.push(stageSLA);
      }
    } catch (e) {
      console.error('Failed to parse stage SLA:', e);
    }
  }
  
  return stageSLAs;
}

// =============================================================================
// Escalation Rules Parser
// =============================================================================

function parseEscalationRules(content: string): EscalationRule[] {
  const section = extractMainSection(content, 'ESCALATION RULES');
  if (!section) {
    console.warn('SLA Catalogue: Missing ESCALATION RULES section');
    return [];
  }
  
  const yamlData = parseYamlBlock(section);
  if (!yamlData || !yamlData.escalationRules) {
    return [];
  }
  
  const rulesRaw = yamlData.escalationRules as Array<Record<string, unknown>>;
  
  return rulesRaw.map(rule => ({
    id: String(rule.id || ''),
    name: String(rule.name || ''),
    nameAr: String(rule.nameAr || ''),
    trigger: String(rule.trigger || ''),
    triggerAr: String(rule.triggerAr || ''),
    triggerType: (rule.triggerType as EscalationTriggerType) || 'warning',
    thresholdPercent: rule.thresholdPercent ? Number(rule.thresholdPercent) : undefined,
    escalateTo: String(rule.escalateTo || ''),
    escalateToAr: String(rule.escalateToAr || ''),
    actions: (rule.actions as string[]) || [],
    actionsAr: (rule.actionsAr as string[]) || [],
    autoReassign: Boolean(rule.autoReassign),
    notifyChannels: (rule.notifyChannels as string[]) || [],
  })).filter(rule => rule.id);
}

// =============================================================================
// Exception Codes Parser
// =============================================================================

function parseExceptionCodes(content: string): SLAExceptionCode[] {
  const section = extractMainSection(content, 'EXCEPTION CODES');
  if (!section) {
    console.warn('SLA Catalogue: Missing EXCEPTION CODES section');
    return [];
  }
  
  const yamlData = parseYamlBlock(section);
  if (!yamlData || !yamlData.exceptionCodes) {
    return [];
  }
  
  const codesRaw = yamlData.exceptionCodes as Array<Record<string, unknown>>;
  
  return codesRaw.map(code => ({
    code: String(code.code || ''),
    label: String(code.label || ''),
    labelAr: String(code.labelAr || ''),
    description: String(code.description || ''),
    descriptionAr: String(code.descriptionAr || ''),
    slaBehavior: (code.slaBehavior as SLABehavior) || 'pause',
    validForStages: (code.validForStages as string[]) || [],
    requiresApproval: Boolean(code.requiresApproval),
    approver: code.approver ? String(code.approver) : undefined,
    approverAr: code.approverAr ? String(code.approverAr) : undefined,
    requiredFields: (code.requiredFields as string[]) || [],
  })).filter(code => code.code);
}

// =============================================================================
// Main Parser Function
// =============================================================================

/**
 * Parse the complete SLA Catalogue registry
 */
function parseSLACatalogueRegistry(markdown: string): SLACatalogueRegistry {
  const docInfo = parseDocumentInfo(markdown);
  const globalConfig = parseGlobalConfig(markdown);
  const channelSLAs = parseChannelSLAs(markdown);
  const stageSLAs = parseStageSLAs(markdown);
  const escalationRules = parseEscalationRules(markdown);
  const exceptionCodes = parseExceptionCodes(markdown);
  
  return {
    version: docInfo.version,
    lastUpdated: docInfo.lastUpdated,
    description: docInfo.description,
    descriptionAr: docInfo.descriptionAr,
    globalConfig,
    channelSLAs,
    stageSLAs,
    escalationRules,
    exceptionCodes,
  };
}

// =============================================================================
// Cached Registry & Export Functions
// =============================================================================

// Parse the registry on module load
let cachedRegistry: SLACatalogueRegistry | null = null;

/**
 * Get the parsed SLA Catalogue Registry
 */
export function getSLACatalogueRegistry(): SLACatalogueRegistry {
  if (!cachedRegistry) {
    cachedRegistry = parseSLACatalogueRegistry(slaCatalogueMarkdown);
  }
  return cachedRegistry;
}

/**
 * Get global SLA configuration
 */
export function getGlobalConfig(): SLAGlobalConfig {
  return getSLACatalogueRegistry().globalConfig;
}

/**
 * Get all channel SLAs
 */
export function getChannelSLAs(): ChannelSLA[] {
  return getSLACatalogueRegistry().channelSLAs;
}

/**
 * Get a specific channel SLA by ID
 */
export function getChannelSLAById(id: string): ChannelSLA | undefined {
  return getChannelSLAs().find(sla => sla.id === id);
}

/**
 * Get channel SLAs by channel type
 */
export function getChannelSLAByType(channelType: LeadChannel): ChannelSLA | undefined {
  return getChannelSLAs().find(sla => sla.channelType === channelType);
}

/**
 * Get all stage SLAs
 */
export function getStageSLAs(): StageSLA[] {
  return getSLACatalogueRegistry().stageSLAs;
}

/**
 * Get a specific stage SLA by ID
 */
export function getStageSLAById(id: string): StageSLA | undefined {
  return getStageSLAs().find(sla => sla.id === id);
}

/**
 * Get stage SLA by pipeline stage ID
 */
export function getStageSLAByStageId(stageId: string): StageSLA | undefined {
  return getStageSLAs().find(sla => sla.stageId === stageId);
}

/**
 * Get all escalation rules
 */
export function getEscalationRules(): EscalationRule[] {
  return getSLACatalogueRegistry().escalationRules;
}

/**
 * Get escalation rules by trigger type
 */
export function getEscalationRulesByType(triggerType: EscalationTriggerType): EscalationRule[] {
  return getEscalationRules().filter(rule => rule.triggerType === triggerType);
}

/**
 * Get all exception codes
 */
export function getExceptionCodes(): SLAExceptionCode[] {
  return getSLACatalogueRegistry().exceptionCodes;
}

/**
 * Get exception code by code string
 */
export function getExceptionCodeByCode(code: string): SLAExceptionCode | undefined {
  return getExceptionCodes().find(exc => exc.code === code);
}

/**
 * Get exception codes valid for a specific stage
 */
export function getExceptionCodesForStage(stageId: string): SLAExceptionCode[] {
  return getExceptionCodes().filter(exc => 
    exc.validForStages.includes('all') || exc.validForStages.includes(stageId)
  );
}

/**
 * Filter channel SLAs by brand
 */
export function getChannelSLAsForBrand(brand: PipelineBrand): ChannelSLA[] {
  return getChannelSLAs().filter(sla => sla.appliesToBrands.includes(brand));
}

/**
 * Filter stage SLAs by brand
 */
export function getStageSLAsForBrand(brand: PipelineBrand): StageSLA[] {
  return getStageSLAs().filter(sla => sla.appliesToBrands.includes(brand));
}

/**
 * Get all SLAs for a specific brand (both channel and stage)
 */
export function getAllSLAsForBrand(brand: PipelineBrand): {
  channelSLAs: ChannelSLA[];
  stageSLAs: StageSLA[];
} {
  return {
    channelSLAs: getChannelSLAsForBrand(brand),
    stageSLAs: getStageSLAsForBrand(brand),
  };
}

/**
 * Get counts for summary display
 */
export function getSLACounts(): {
  totalSLAs: number;
  channelSLAs: number;
  stageSLAs: number;
  escalationRules: number;
  exceptionCodes: number;
} {
  const registry = getSLACatalogueRegistry();
  return {
    totalSLAs: registry.channelSLAs.length + registry.stageSLAs.length,
    channelSLAs: registry.channelSLAs.length,
    stageSLAs: registry.stageSLAs.length,
    escalationRules: registry.escalationRules.length,
    exceptionCodes: registry.exceptionCodes.length,
  };
}

/**
 * Export the raw registry for debugging
 */
export const registry = getSLACatalogueRegistry();

export default registry;
